import {
  IPeopleProfile,
  PeopleFilterType,
} from '@app/core/shared/interfaces/solutions-profile.interface';
import { cleanPeopleProfileName, normalizePeopleFilterType } from './normalize-people-profile.util';

export { canViewSolutionsProfileDetails, userIsSolutionsAdmin } from './solutions-profile-access.util';

export function shouldBlurProfileField(
  canViewDetails: boolean,
  hasRealValue: boolean
): boolean {
  return !canViewDetails && !hasRealValue;
}

export function profileHasRealPhone(
  profile: IPeopleProfile
): boolean {
  const phone = profile.contact_phone ?? profile.phone;
  return !!(phone?.prefix?.trim() && phone?.mobile_number?.trim());
}

const FAKE_BIOS = [
  'Experienced professional delivering quality projects across commercial and residential sectors.',
  'Specialist with a strong track record in specification-led design and on-site delivery.',
  'Trusted partner for end-to-end supply, installation, and aftercare support.',
  'Focused on reliable workmanship, clear communication, and long-term client relationships.',
];

const FAKE_REGIONS = [
  'Greater London',
  'West Midlands',
  'Greater Manchester',
  'West Yorkshire',
  'Bristol Area',
  'Merseyside',
];

function hashSeed(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function pickFrom<T>(items: T[], seed: string): T {
  return items[hashSeed(seed) % items.length];
}

export function profileDisplaySeed(profile: IPeopleProfile): string {
  return profile._id ?? profile.contact_email ?? profile.exact_role ?? 'profile';
}

export function profileRealFirstName(profile: IPeopleProfile): string {
  const fromApi = cleanPeopleProfileName(profile.first_name);
  if (fromApi) return fromApi;

  const responsible = cleanPeopleProfileName(profile.responsible_person);
  if (responsible) return responsible.split(/\s+/)[0] ?? '';

  return '';
}

export function resolveProfilePeopleType(
  profile: IPeopleProfile,
  fallback: PeopleFilterType = 'SUPPLIER'
): PeopleFilterType {
  return normalizePeopleFilterType(profile.type as string) ?? fallback;
}

export function profileTypeTranslationKey(type: PeopleFilterType): string {
  return `solutions.profileTypes.${type}`;
}

const FAKE_PROJECTS = [
  'Commercial fit-out and stone installation',
  'Residential facade and cladding package',
  'Mixed-use development supply and install',
];

export function profileDisplayFakeProjectSummaries(): string[] {
  return FAKE_PROJECTS;
}

export function profileDisplayFakeProjectSummary(seed: string): string {
  return pickFrom(FAKE_PROJECTS, `${seed}-project`);
}

export function profileDisplayBio(
  profile: IPeopleProfile,
  canViewDetails: boolean,
  realBio: string
): string {
  const trimmed = realBio.trim();
  if (canViewDetails || trimmed) {
    return trimmed;
  }
  return pickFrom(FAKE_BIOS, `${profileDisplaySeed(profile)}-bio`);
}

export function profileDisplayRegion(
  profile: IPeopleProfile,
  canViewDetails: boolean
): string {
  const real = profile.operating_city_region?.trim() ?? '';
  if (canViewDetails || real) {
    return real;
  }
  return pickFrom(FAKE_REGIONS, `${profileDisplaySeed(profile)}-region`);
}

export function profileDisplayEmail(
  profile: IPeopleProfile,
  canViewDetails: boolean
): string {
  const real = profile.contact_email?.trim() ?? '';
  if (canViewDetails || real) {
    return real;
  }
  const seed = hashSeed(`${profileDisplaySeed(profile)}-email`);
  return `contact${seed % 900 + 100}@example.com`;
}

export function profileDisplayPhone(
  profile: IPeopleProfile,
  canViewDetails: boolean
): string {
  const phone = profile.contact_phone ?? profile.phone;
  if (canViewDetails || profileHasRealPhone(profile)) {
    if (!phone?.prefix || !phone?.mobile_number) {
      return '';
    }
    return `+${phone.prefix} ${phone.mobile_number}`;
  }
  const seed = hashSeed(`${profileDisplaySeed(profile)}-phone`);
  return `+44 7${String(seed).slice(0, 3)} ${String(seed).slice(3, 6)} ${String(seed).slice(6, 9)}`.slice(
    0,
    16
  );
}

export function profileDisplayAddress(
  profile: IPeopleProfile,
  canViewDetails: boolean,
  realAddress: string
): string {
  const trimmed = realAddress.trim();
  if (canViewDetails || trimmed) {
    return trimmed;
  }
  return `${pickFrom(FAKE_REGIONS, `${profileDisplaySeed(profile)}-addr`)}, United Kingdom`;
}

export function profileDisplayWebsite(canViewDetails: boolean): string {
  if (canViewDetails) {
    return '';
  }
  return 'www.example-business.co.uk';
}

export function profileHasContactSection(
  profile: IPeopleProfile,
  canViewDetails: boolean
): boolean {
  if (canViewDetails) {
    return !!(profile.contact_email?.trim() || profileHasRealPhone(profile));
  }
  if (profile.contact_email?.trim() || profileHasRealPhone(profile)) {
    return true;
  }
  return true;
}

export function profileHasBioSection(
  profile: IPeopleProfile,
  canViewDetails: boolean,
  realBio: string
): boolean {
  const trimmed = realBio.trim();
  if (canViewDetails) {
    return !!trimmed;
  }
  if (trimmed) {
    return true;
  }
  return true;
}

export function profileHasRegionSection(
  profile: IPeopleProfile,
  canViewDetails: boolean
): boolean {
  if (canViewDetails) {
    return !!profile.operating_city_region?.trim();
  }
  if (profile.operating_city_region?.trim()) {
    return true;
  }
  return true;
}

export function profileHasAddressSection(
  profile: IPeopleProfile,
  canViewDetails: boolean,
  realAddress: string
): boolean {
  const trimmed = realAddress.trim();
  if (canViewDetails) {
    return !!trimmed;
  }
  if (trimmed) {
    return true;
  }
  return true;
}
